import TextM from "@common/components/text/TextM"
import { useQuizCorrectionData } from "@common/hooks/useQuizCorrectionData"
import { theme } from "@common/theme/theme"
import React from "react"
import { ActivityIndicator, Dimensions, Platform, ScrollView, View } from "react-native"
import PrerequisiteQuizDisplay from "../../../reviewer/PrerequisiteQuizDisplay"

const CORRECTION_HEIGHT_RATIO = Platform.OS === "web" ? 0.62 : 0.42

/**
 * QuizCorrectionReview — student-facing wrapper around the shared
 * PrerequisiteQuizDisplay (the same component used on the tutor side).
 *
 * Renders the full correction — every question, not only the mistakes — under
 * the "Ta correction" heading. Reads either a provided `data` (a QuizResource
 * shape, for instant render right after submit) or fetches it from
 * `activityId` (retroactive view).
 *
 * No modal wrapper — designed to be placed directly inside another screen.
 */
const QuizCorrectionReview = ({
    activityId,
    data: providedData,
    style,
    // Share of the viewport the scrollable correction may take. The default
    // suits Sam, where the champion bubble already eats half the screen; the
    // modal passes a smaller one because the score ring sits above it.
    heightRatio = CORRECTION_HEIGHT_RATIO,
}) => {
    const { data, loading, error } = useQuizCorrectionData({
        activityId,
        providedData,
    })

    if (loading && !data) {
        return (
            <View style={[{ paddingVertical: 12 }, style]}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
            </View>
        )
    }

    if (error && !data) {
        return (
            <View style={[{ paddingVertical: 8 }, style]}>
                <TextM color={theme.colors.error} wrap fontSize={13}>
                    {error}
                </TextM>
            </View>
        )
    }

    if (!data) {
        return null
    }

    return (
        <View style={[{ width: "100%" }, style]}>
            {/* Bounded and self-scrolling: whatever sits below — the "Continuer"
                button in Sam — must stay put instead of being pushed off screen
                as the student expands questions. The share of the viewport is
                larger on the web, where the checkpoint sits in a tall window and
                a phone-sized cap would leave a wide empty band under the button. */}
            <ScrollView
                style={{ maxHeight: Dimensions.get("window").height * heightRatio }}
                nestedScrollEnabled
                showsVerticalScrollIndicator
            >
                <PrerequisiteQuizDisplay
                    quiz={data}
                    showScore={false}
                    showFeedback={false}
                    showSignalButton={false}
                    scrollable={false}
                    wrapPreview={true}
                    title="Ta correction"
                />
            </ScrollView>
        </View>
    )
}

export default QuizCorrectionReview
